import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  CheckCircle2,
  ShoppingBag,
  Shield,
  FileText,
  Clock,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import DocumentRequiredModal from "../common/DocumentRequiredModal";
import quoteService from "../../api/QuoteService";
import { formatMoney } from "../../utils/formatMoney";

// Dynamic packages will be generated from policy data

const tabs = [
  { key: "premium", label: "Policy Premium" },
  { key: "covered", label: "What is covered" },
  { key: "claims", label: "Claim process overview" },
  { key: "terms", label: "Terms & Condition" },
];

// Helper function to format duration strings (e.g., "3 yearly" → "3 years", "1 yearly" → "1 year")
const formatDurationString = (durationStr) => {
  if (!durationStr || durationStr === "N/A") return durationStr;
  
  const str = String(durationStr).trim();
  
  // Match patterns like "3 yearly", "2 monthly", "1 yearly", "5 weekly", etc.
  const match = str.match(/^(\d+)\s*(yearly|monthly|weekly|daily|years|months|weeks|days|hours|hour|day|week|month|year)$/i);
  
  if (!match) {
    // If it doesn't match the pattern, try to handle other formats
    // Check if it already has proper format like "2 years", "1 month"
    const alreadyFormatted = str.match(/^(\d+)\s*(years?|months?|weeks?|days?|hours?)$/i);
    if (alreadyFormatted) {
      return str; // Already properly formatted
    }
    return str; // Return as-is if we can't parse it
  }
  
  const value = parseInt(match[1], 10);
  let unit = match[2].toLowerCase();
  
  // Normalize unit names (yearly → year, monthly → month, etc.)
  const unitMap = {
    'yearly': 'year',
    'monthly': 'month',
    'weekly': 'week',
    'daily': 'day',
    'hours': 'hour',
    'hour': 'hour',
    'days': 'day',
    'day': 'day',
    'weeks': 'week',
    'week': 'week',
    'months': 'month',
    'month': 'month',
    'years': 'year',
    'year': 'year'
  };
  
  unit = unitMap[unit] || unit;
  
  // Pluralize if value > 1
  if (value > 1) {
    unit = unit + 's';
  }
  
  return `${value} ${unit}`;
};

export default function PackageDrawer({ open, onClose, item }) {
  const [selectedPkg, setSelectedPkg] = useState("");
  const [activeTab, setActiveTab] = useState("premium");
  const [selectedRouteType, setSelectedRouteType] = useState("regional");
  const providerSlug = item?.originalPolicy?.insurance_provider?.slug || null;
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({});
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  // Check if this is Life, General, Travel, or TripSecure Insurance
  const isTripSecureInsurance = item?.shortDescription === "tripsecure_insurance";
  const isLifeInsurance = item?.shortDescription === "life_insurance";
  const isGeneralInsurance = item?.shortDescription === "general_insurance";
  const isTravelInsurance = item?.shortDescription === "travel_insurance";
  const isRouteBasedInsurance = isTripSecureInsurance || (isTravelInsurance && !isLifeInsurance && !isGeneralInsurance);

  const isDocumentComplete = useMemo(() => {
    if (!user) return false;
    return Boolean(
      user.document_type &&
        user.card_number &&
        user.issue_date &&
        user.expiry_date &&
        user.id_card_front &&
        user.id_card_back
    );
  }, [user]);

  // Generate dynamic packages from policy data and group by route type
  const { packages, routeTypes } = useMemo(() => {
    if (!item?.originalPolicy?.coverage_durations)
      return { packages: [], routeTypes: [] };

    // Handle Life, General, and Travel Insurance packages
    if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
      const allPackages = item.originalPolicy.coverage_durations.map(
        (pkg, index) => {
          return {
            key: `pkg-${index}-${pkg.product_name?.replace(/\s+/g, "-") || index}`,
            label: pkg.product_name || `Package ${index + 1}`,
            productName: pkg.product_name || `Package ${index + 1}`,
            premium: pkg.premium || 0,
            premiumDuration: pkg.premium_duration || "monthly",
            duration: pkg.duration || "N/A",
            description: pkg.description || "",
            active: index === 0,
            fullPackage: pkg,
            is_quote_only: !!pkg.is_quote_only,
            quote_request_fields: pkg.quote_request_fields || [],
          };
        }
      );

      return {
        packages: allPackages,
        routeTypes: [],
      };
    }

    // Handle TripSecure and Travel Insurance (route-based logic)
    const isIntercityPolicy =
      item?.originalPolicy?.policy_type === "intercity_travel";

    const allPackages = item.originalPolicy.coverage_durations.map(
      (duration, index) => {
        // Helper to format duration label
        const formatDurationLabel = (duration) => {
          if (duration && typeof duration === "object") {
            // Handle new format with display_unit and display_value
            if (duration.display_unit && duration.display_value) {
              const unit = duration.display_unit;
              const value = duration.display_value;
              return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`;
            }
            // Handle old format with days
            if (duration.days) {
              return `${duration.days} ${duration.days === 1 ? "Day" : "Days"}`;
            }
            // Handle hours format
            if (duration.hours) {
              const hours = duration.hours;
              return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
            }
          }
          return duration ? duration.toString() : "Unknown";
        };

        const getDurationValue = (duration) => {
          if (duration && typeof duration === "object") {
            if (duration.display_value !== undefined)
              return duration.display_value;
            if (duration.days !== undefined) return duration.days;
            if (duration.hours !== undefined) return duration.hours;
          }
          return 1;
        };

        const getDurationUnit = (duration) => {
          if (duration && typeof duration === "object") {
            if (duration.display_unit) return duration.display_unit;
            if (duration.days !== undefined) return "days";
            if (duration.hours !== undefined) return "hours";
          }
          return "days";
        };

        const durationValue = getDurationValue(duration);
        const durationUnit = getDurationUnit(duration);
        const durationLabel = formatDurationLabel(duration);

        // For intercity policies, include route information in the label
        let packageLabel;
        if (isIntercityPolicy && duration.route_destination) {
          packageLabel = duration.route_destination;
        } else {
          packageLabel = `${durationLabel} Cover`;
        }

        return {
          key: `${index}-${durationValue}${durationUnit.charAt(0)}${
            duration.route_destination
              ? `-${duration.route_destination.replace(/\s+/g, "-")}`
              : ""
          }`,
          label: packageLabel,
          durationLabel: durationLabel,
          price: duration.price,
          days: durationValue,
          unit: durationUnit,
          routeOrigin: isIntercityPolicy ? "Greater Accra" : null,
          routeDestination: duration.route_destination || null,
          routeType: duration.route_type || null,
          active: index === 0,
          fullDuration: duration,
        };
      }
    );

    // Extract unique route types if this is an intercity policy (for TripSecure or old Travel Insurance)
    const uniqueRouteTypes = isIntercityPolicy
      ? [...new Set(allPackages.map((p) => p.routeType).filter(Boolean))]
      : [];

    return {
      packages: allPackages,
      routeTypes: uniqueRouteTypes,
    };
  }, [
    item?.originalPolicy?.coverage_durations,
    item?.originalPolicy?.policy_type,
    isLifeInsurance,
    isGeneralInsurance,
    isTravelInsurance,
  ]);

  // Filter packages by selected route type (for TripSecure and Travel insurance)
  const filteredPackages = useMemo(() => {
    if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) return packages;
    if (routeTypes.length === 0) return packages;
    return packages.filter((pkg) => pkg.routeType === selectedRouteType);
  }, [packages, selectedRouteType, routeTypes, isLifeInsurance, isGeneralInsurance, isTravelInsurance]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setActiveTab("premium");

    // Set initial package
    if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
      // For Life/General/Travel Insurance, just select the first package
      setSelectedPkg(packages[0]?.key || "");
    } else if (isTripSecureInsurance && routeTypes.length > 0) {
      // For TripSecure Insurance with routes (old Travel Insurance design)
      setSelectedRouteType(routeTypes[0]);
      const firstPackageOfType = packages.find(
        (p) => p.routeType === routeTypes[0]
      );
      setSelectedPkg(firstPackageOfType?.key || "");
    } else if (isTripSecureInsurance) {
      // For TripSecure Insurance without route types (e.g., okada_rider)
      setSelectedPkg(packages[0]?.key || "");
    } else {
      setSelectedPkg(packages[0]?.key || "");
    }

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, item, packages, routeTypes, isLifeInsurance, isGeneralInsurance, isTravelInsurance, isTripSecureInsurance]);

  // Update selected package when route type changes
  useEffect(() => {
    if (
      filteredPackages.length > 0 &&
      !filteredPackages.find((p) => p.key === selectedPkg)
    ) {
      setSelectedPkg(filteredPackages[0]?.key || "");
    }
  }, [selectedRouteType, filteredPackages, selectedPkg]);
  return (
    <AnimatePresence>
      {open && (
        <>
          <DocumentRequiredModal
            isOpen={showDocumentModal}
            onClose={() => setShowDocumentModal(false)}
            onGoToProfile={() => {
              setShowDocumentModal(false);
              navigate("/settings");
            }}
            title="Documents Required"
            message="Please upload your required documents in your profile before purchasing this policy."
          />
          <motion.div
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.aside
            className="fixed inset-y-0 right-0 z-[70] w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full"
            initial={{ x: 420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Header */}
            <div className="flex justify-between items-start p-5 border-b border-gray-200/70">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {item?.policyName || item?.provider}
                </h3>
                {item?.originalPolicy?.insurance_provider && (
                  <div className="flex gap-2 items-center mt-1">
                    <span className="text-xs text-gray-500">Provided by:</span>
                    <span className="text-xs font-semibold text-emerald-700">
                      {typeof item.originalPolicy.insurance_provider === 'object' 
                        ? item.originalPolicy.insurance_provider?.company_name 
                        : item.originalPolicy.insurance_provider}
                    </span>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {item?.shortDescription}
                </p>
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 p-2 text-gray-500 rounded-lg hover:bg-gray-100"
                aria-label="Close package details"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto overscroll-contain flex-1 p-4 space-y-4 min-h-0">
              {/* Package options - Grid Layout */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Select Coverage
                  </h4>
                  <button
                    onClick={() => {
                      const selectedPackage = filteredPackages.find(
                        (p) => p.key === selectedPkg
                      );

                      // Quote-based package: open Request Quote modal instead of checkout
                      if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                        if (selectedPackage?.is_quote_only) {
                          setShowQuoteModal(true);
                          setQuoteFormData({});
                          return;
                        }
                      }

                      // Handle Life, General, and Travel Insurance differently
                      if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                        const coverLabel = selectedPackage?.productName || "Package";
                        // Extract insurance_provider_slug
                        const insuranceProviderSlug = item?.originalPolicy?.insurance_provider 
                          ? (typeof item.originalPolicy.insurance_provider === 'object' 
                              ? item.originalPolicy.insurance_provider.slug || item.originalPolicy.insurance_provider.id || null
                              : item.originalPolicy.insurance_provider)
                          : null;
                        const details = {
                          policyType: item?.originalPolicy?.policy_type || 
                            (isLifeInsurance ? "life_insurance" : 
                             isGeneralInsurance ? "general_insurance" : 
                             "travel_insurance"),
                          providerName: item?.provider || "",
                          policyName: item?.policyName || "",
                          coverLabel,
                          amount: selectedPackage?.premium ?? item?.premiumMin ?? 0,
                          duration: selectedPackage?.duration || "N/A",
                          policySlug: item?.originalPolicy?.slug || item?.id,
                          originalPolicy: item?.originalPolicy,
                          selectedPackage: selectedPackage?.fullPackage || null,
                          insuranceType: isLifeInsurance ? "life_insurance" : 
                                        isGeneralInsurance ? "general_insurance" : 
                                        "travel_insurance",
                          // For travel insurance, include the coverage duration
                          coverageDuration: isTravelInsurance ? selectedPackage?.fullPackage || null : null,
                          insurance_provider_slug: insuranceProviderSlug,
                        };
                        
                        if (!isAuthenticated) {
                          sessionStorage.setItem(
                            "pendingCheckout",
                            JSON.stringify(details)
                          );
                          navigate("/signin", { state: { from: "/checkout" } });
                          return;
                        }
                        if (!isDocumentComplete) {
                          setShowDocumentModal(true);
                          return;
                        }
                        navigate("/checkout", { state: details });
                        return;
                      }
                      
                      // Handle TripSecure and Travel Insurance (route-based logic)
                      const coverLabel = selectedPackage?.label || "Cover";
                      const id = item?.id || "";
                      const policyType = item?.originalPolicy?.policy_type;
                      const isIntercity = policyType === "intercity_travel" || id.startsWith("intercity-");
                      const isOkada = policyType === "okada_rider" || id.startsWith("bikers-");
                      const isDailyCommuter = policyType === "daily_commuter" || id.startsWith("daily-");
                      const isTaxi =
                        id.startsWith("taxi-") || id.startsWith("drivers-");
                      // Find the selected coverage duration from the original policy
                      const selectedCoverageDuration =
                        selectedPackage?.fullDuration ||
                        item?.originalPolicy?.coverage_durations?.find(
                          (duration) => {
                            if (
                              duration.display_unit &&
                              duration.display_value
                            ) {
                              const matchesDuration =
                                duration.display_value ===
                                  selectedPackage?.days &&
                                duration.display_unit === selectedPackage?.unit;
                              const matchesRoute =
                                !duration.route_destination ||
                                duration.route_destination ===
                                  selectedPackage?.routeDestination;
                              return matchesDuration && matchesRoute;
                            } else if (duration.days) {
                              return duration.days === selectedPackage?.days;
                            } else if (duration.hours) {
                              return duration.hours === selectedPackage?.days;
                            }
                            return false;
                          }
                        );

                      // Extract insurance_provider_slug
                      const insuranceProviderSlug = item?.originalPolicy?.insurance_provider 
                        ? (typeof item.originalPolicy.insurance_provider === 'object' 
                            ? item.originalPolicy.insurance_provider.slug || null
                            : item.originalPolicy.insurance_provider || null)
                        : providerSlug;
                      const details = {
                        policyType:
                          item?.originalPolicy?.policy_type || "daily_commuter",
                        providerName: item?.provider || "",
                        policyName: item?.policyName || "",
                        coverLabel,
                        amount: selectedPackage?.price ?? item?.premiumMin ?? 0,
                        days: selectedPackage?.days ?? 2,
                        policySlug: item?.originalPolicy?.slug || item?.id,
                        originalPolicy: item?.originalPolicy,
                        coverageDuration: selectedCoverageDuration || null,
                        insurance_provider_slug: insuranceProviderSlug || providerSlug,
                      };
                      // Navigate directly to checkout - checkout handles form rendering based on policyType
                      if (isIntercity || isOkada || isDailyCommuter) {
                        if (!isAuthenticated) {
                          sessionStorage.setItem(
                            "pendingCheckout",
                            JSON.stringify(details)
                          );
                          navigate("/signin", {
                            state: { from: "/checkout" },
                          });
                          return;
                        }
                        if (!isDocumentComplete) {
                          setShowDocumentModal(true);
                          return;
                        }
                        navigate("/checkout", { state: details });
                        return;
                      }
                      if (isTaxi) {
                        if (!isAuthenticated) {
                          sessionStorage.setItem(
                            "pendingCheckout",
                            JSON.stringify(details)
                          );
                          navigate("/signin", {
                            state: { from: "/taxi/form" },
                          });
                          return;
                        }
                        if (!isDocumentComplete) {
                          setShowDocumentModal(true);
                          return;
                        }
                        navigate("/taxi/form", { state: details });
                        return;
                      }
                      if (!isAuthenticated) {
                        sessionStorage.setItem(
                          "pendingCheckout",
                          JSON.stringify(details)
                        );
                        navigate("/signin", { state: { from: "/checkout" } });
                        return;
                      }
                      if (!isDocumentComplete) {
                        setShowDocumentModal(true);
                        return;
                      }
                      navigate("/checkout", { state: details });
                    }}
                    className={`inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-md transition-all ${
                      (isLifeInsurance || isGeneralInsurance || isTravelInsurance) &&
                      filteredPackages.find((p) => p.key === selectedPkg)?.is_quote_only
                        ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                        : "bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                    }`}
                  >
                    {(isLifeInsurance || isGeneralInsurance || isTravelInsurance) &&
                    filteredPackages.find((p) => p.key === selectedPkg)?.is_quote_only ? (
                      <>
                        <Send size={16} /> Request a Quote
                      </>
                    ) : (
                      <>
                        <ShoppingBag size={16} /> Buy Now
                      </>
                    )}
                  </button>
                </div>

                {/* Route Type Tabs - Show for TripSecure and Travel insurance with multiple route types */}
                {isRouteBasedInsurance && routeTypes.length > 0 && (
                  <div className="flex overflow-x-auto gap-2 no-scrollbar">
                    {routeTypes.map((routeType) => (
                      <button
                        key={routeType}
                        onClick={() => setSelectedRouteType(routeType)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-all ${
                          selectedRouteType === routeType
                            ? "bg-emerald-500 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {routeType.charAt(0).toUpperCase() + routeType.slice(1)}{" "}
                        Routes
                      </button>
                    ))}
                  </div>
                )}

                {/* Package Grid */}
                <div className={`grid gap-2 ${isLifeInsurance || isGeneralInsurance || isTravelInsurance ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {filteredPackages.map((p) => {
                    const isActive = selectedPkg === p.key;
                    const accentColor = isLifeInsurance 
                      ? "blue" 
                      : isGeneralInsurance 
                      ? "purple" 
                      : "emerald";
                    
                    return (
                      <label key={p.key} className="cursor-pointer">
                        <input
                          type="radio"
                          name="pkg"
                          checked={isActive}
                          onChange={() => setSelectedPkg(p.key)}
                          className="sr-only"
                        />
                        <div
                          className={`relative rounded-lg border text-sm transition-all p-4 h-full ${
                            isActive
                              ? (accentColor === "blue" 
                                  ? "border-blue-500 ring-2 shadow-md ring-blue-500/20"
                                  : accentColor === "purple"
                                  ? "border-purple-500 ring-2 shadow-md ring-purple-500/20"
                                  : "border-emerald-500 ring-2 shadow-md ring-emerald-500/20")
                              : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                          }`}
                        >
                          {isActive && (
                            <motion.div
                              layoutId="pkgHighlight"
                              className={`absolute inset-0 bg-gradient-to-br rounded-lg ${
                                accentColor === "blue"
                                  ? "from-blue-50 to-indigo-50"
                                  : accentColor === "purple"
                                  ? "from-purple-50 to-violet-50"
                                  : "from-emerald-50 to-green-50"
                              }`}
                            />
                          )}
                          <div className="flex relative z-10 flex-col gap-2">
                            {/* For Life/General/Travel Insurance: Show product name, premium, duration, description */}
                            {(isLifeInsurance || isGeneralInsurance || isTravelInsurance) ? (
                              <>
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <h5 className={`font-bold text-base ${
                                      isActive 
                                        ? (accentColor === "blue" ? "text-blue-700" : accentColor === "purple" ? "text-purple-700" : "text-emerald-700")
                                        : "text-gray-900"
                                    }`}>
                                      {p.productName}
                                    </h5>
                                    {p.description && (
                                      <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                        {p.description}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex-shrink-0 ml-3 text-right">
                                    <div className="flex flex-col items-end">
                                      {p.is_quote_only ? (
                                        <span className={`text-sm font-semibold ${
                                          isActive
                                            ? (accentColor === "blue" ? "text-blue-600" : accentColor === "purple" ? "text-purple-600" : "text-amber-600")
                                            : "text-amber-700"
                                        }`}>
                                          Price on quote
                                        </span>
                                      ) : (
                                        <>
                                          <span className={`text-2xl font-bold ${
                                            isActive 
                                              ? (accentColor === "blue" ? "text-blue-600" : accentColor === "purple" ? "text-purple-600" : "text-emerald-600")
                                              : "text-gray-900"
                                          }`}>
                                            {formatMoney(p.premium)}
                                          </span>
                                          <span className={`text-xs font-medium capitalize ${
                                            isActive 
                                              ? (accentColor === "blue" ? "text-blue-500" : accentColor === "purple" ? "text-purple-500" : "text-emerald-500")
                                              : "text-gray-500"
                                          }`}>
                                            per {(() => {
                                              const duration = p.premiumDuration || "monthly";
                                              const durationMap = {
                                                'yearly': 'year',
                                                'monthly': 'month',
                                                'weekly': 'week',
                                                'daily': 'day'
                                              };
                                              return durationMap[duration.toLowerCase()] || duration;
                                            })()}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2 items-center pt-2 border-t border-gray-200">
                                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                                    isActive 
                                      ? (accentColor === "blue" 
                                          ? "bg-blue-100 text-blue-700"
                                          : accentColor === "purple"
                                          ? "bg-purple-100 text-purple-700"
                                          : "bg-emerald-100 text-emerald-700")
                                      : "bg-gray-100 text-gray-700"
                                  }`}>
                                    <Clock size={12} className="mr-1" />
                                    {formatDurationString(p.duration)}
                                  </span>
                                </div>
                              </>
                            ) : (
                              /* For Travel Insurance: Show route or duration */
                              <>
                                {p.routeOrigin && p.routeDestination ? (
                                  <div className="flex gap-1 items-center">
                                    <span
                                      className={`font-semibold text-xs ${
                                        isActive ? "text-emerald-700" : "text-gray-900"
                                      }`}
                                    >
                                      {p.routeOrigin}
                                    </span>
                                    <svg
                                      className={`w-3 h-3 ${
                                        isActive ? "text-emerald-600" : "text-gray-400"
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                                      />
                                    </svg>
                                    <span
                                      className={`font-semibold text-xs ${
                                        isActive ? "text-emerald-700" : "text-gray-900"
                                      }`}
                                    >
                                      {p.routeDestination}
                                    </span>
                                  </div>
                                ) : (
                                  <span
                                    className={`font-semibold text-sm ${
                                      isActive ? "text-emerald-700" : "text-gray-900"
                                    }`}
                                  >
                                    {p.label}
                                  </span>
                                )}
                                {p.routeDestination && (
                                  <span className="text-xs text-gray-600">
                                    {p.durationLabel}
                                  </span>
                                )}
                                <div className="flex gap-1 items-baseline mt-1">
                                  <span
                                    className={`text-lg font-bold ${
                                      isActive
                                        ? "text-emerald-600"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {formatMoney(p.price)}
                                  </span>
                                  {p.routeType && (
                                    <span className="text-xs text-gray-500 capitalize">
                                      • {p.routeType}
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-3">
                <div className="flex overflow-x-auto relative gap-2 items-center whitespace-nowrap border-b border-gray-200 no-scrollbar">
                  {tabs.map((t) => {
                    const active = activeTab === t.key;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`relative px-3 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? "text-emerald-700"
                            : "text-gray-600 hover:text-emerald-600"
                        }`}
                      >
                        {t.label}
                        {active && (
                          <motion.span
                            layoutId="tabUnderline"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-full"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {activeTab === "premium" && (
                  <motion.div
                    key="premium"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60"
                  >
                    <div className="flex gap-2 items-start">
                      <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                        <CheckCircle2 size={14} />
                      </span>
                      <div className="flex-1 space-y-2">
                        <p className="text-xs font-semibold text-gray-900">
                          Premium Details
                        </p>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-100">
                            {(() => {
                              const selectedPackage = filteredPackages.find(
                                (p) => p.key === selectedPkg
                              );
                              if (selectedPackage) {
                                if (selectedPackage.is_quote_only) {
                                  return "Price provided after you request a quote";
                                }
                                if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                                  const duration = selectedPackage.premiumDuration || "monthly";
                                  const durationMap = {
                                    'yearly': 'year',
                                    'monthly': 'month',
                                    'weekly': 'week',
                                    'daily': 'day'
                                  };
                                  const normalizedDuration = durationMap[duration.toLowerCase()] || duration;
                                  return `${formatMoney(selectedPackage.premium)} per ${normalizedDuration}`;
                                }
                                const unit =
                                  selectedPackage.unit === "hours"
                                    ? "hour"
                                    : "day";
                                const pluralUnit =
                                  selectedPackage.days > 1 ? `${unit}s` : unit;
                                return `${formatMoney(selectedPackage.price)} / ${selectedPackage.days} ${pluralUnit}`;
                              }
                              return `From ${formatMoney(item?.premiumMin ?? 5)}`;
                            })()}
                          </span>
                          {(() => {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            if (selectedPackage?.routeOrigin && selectedPackage?.routeDestination) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">
                                  <span>{selectedPackage.routeOrigin}</span>
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                                    />
                                  </svg>
                                  <span>{selectedPackage.routeDestination}</span>
                                </span>
                              );
                            }
                            if (selectedPackage?.routeDestination) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100">
                                  {selectedPackage.routeDestination}
                                </span>
                              );
                            }
                          })()}
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                            No hidden fees
                          </span>
                        </div>

                        {/* Underwriter Information */}
                        <div className="pt-3 mt-3 border-t border-emerald-200/50">
                          <div className="flex gap-2 items-center">
                            <div className="flex flex-shrink-0 justify-center items-center w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full">
                              <Shield size={12} className="text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-700">
                                Underwritten by
                              </p>
                              <p className="text-sm font-bold text-blue-700">
                                {item?.provider}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-2 py-1 text-[10px] font-semibold text-blue-800 bg-blue-100 rounded-full">
                                Licensed
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {activeTab === "covered" && (
                  <motion.div
                    key="covered"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60"
                  >
                    <div className="flex gap-2 items-start mb-2">
                      <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                        <Shield size={14} />
                      </span>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-900">
                          What is covered
                        </h4>
                        <p className="mt-1 text-xs leading-relaxed text-gray-600">
                          {item?.description ||
                            "This policy provides comprehensive coverage for your needs."}
                        </p>
                      </div>
                    </div>
                    <ul className="pl-7 mt-2 space-y-2">
                      {(() => {
                        // For Life, General, and Travel insurance, get package-specific benefits
                        if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                          const selectedPackage = filteredPackages.find(
                            (p) => p.key === selectedPkg
                          );
                          const packageBenefits = selectedPackage?.fullPackage?.insurance_coverage_list;
                          
                          if (packageBenefits && packageBenefits.length > 0) {
                            return packageBenefits.map((coverage, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                <div>
                                  <span className="font-medium text-gray-900">
                                    {coverage.title}
                                  </span>
                                  <p className="mt-0.5 text-gray-600">
                                    {coverage.description}
                                  </p>
                                  {coverage.limit && (
                                    <p className="mt-0.5 font-medium text-emerald-600">
                                      Limit: ₵{coverage.limit}
                                    </p>
                                  )}
                                </div>
                              </li>
                            ));
                          }
                        }
                        
                        // For TripSecure insurance, use policy-level benefits
                        const policyBenefits = item?.originalPolicy?.insurance_coverage_list;
                        if (policyBenefits && policyBenefits.length > 0) {
                          return policyBenefits.map((coverage, index) => (
                            <li
                              key={index}
                              className="flex gap-2 items-start text-xs"
                            >
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <div>
                                <span className="font-medium text-gray-900">
                                  {coverage.title}
                                </span>
                                <p className="mt-0.5 text-gray-600">
                                  {coverage.description}
                                </p>
                                {coverage.limit && (
                                  <p className="mt-0.5 font-medium text-emerald-600">
                                    Limit: ₵{coverage.limit}
                                  </p>
                                )}
                              </div>
                            </li>
                          ));
                        }
                        
                        // Fallback if no benefits found
                        return (
                          <li className="flex gap-2 items-start text-xs text-gray-900">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                            <span>Comprehensive protection coverage</span>
                          </li>
                        );
                      })()}
                    </ul>
                  </motion.div>
                )}

                {activeTab === "claims" && (
                  <motion.div
                    key="claims"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    {/* Claim process */}
                    <div className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Claim Process
                          </h4>
                          <p className="mt-0.5 text-xs text-gray-600">
                            Follow these steps to file a claim
                          </p>
                        </div>
                      </div>
                      <ul className="pl-7 mt-2 space-y-2">
                        {(() => {
                          // For Life, General, and Travel insurance, get package-specific claim steps
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageClaimSteps = selectedPackage?.fullPackage?.claim_process_steps;
                            
                            if (packageClaimSteps && packageClaimSteps.length > 0) {
                              return packageClaimSteps.map((step, index) => (
                                <li
                                  key={index}
                                  className="flex gap-2 items-start text-xs"
                                >
                                  <span className="flex justify-center items-center w-4 h-4 text-[10px] font-semibold text-white bg-emerald-500 rounded-full flex-shrink-0 mt-0.5">
                                    {step.step_number || index + 1}
                                  </span>
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-900">
                                      {step.title}
                                    </span>
                                    <p className="mt-0.5 text-gray-600">
                                      {step.description}
                                    </p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {step.timeframe && (
                                        <span className="px-1.5 py-0.5 text-[10px] text-blue-800 bg-blue-100 rounded">
                                          {step.timeframe}
                                        </span>
                                      )}
                                      {step.requirements && (
                                        <span className="px-1.5 py-0.5 text-[10px] text-gray-800 bg-gray-100 rounded">
                                          {step.requirements}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ));
                            }
                          }
                          
                          // For TripSecure insurance, use policy-level claim steps
                          const policyClaimSteps = item?.originalPolicy?.claim_process_steps;
                          if (policyClaimSteps && policyClaimSteps.length > 0) {
                            return policyClaimSteps.map((step, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="flex justify-center items-center w-4 h-4 text-[10px] font-semibold text-white bg-emerald-500 rounded-full flex-shrink-0 mt-0.5">
                                  {step.step_number || index + 1}
                                </span>
                                <div className="flex-1">
                                  <span className="font-medium text-gray-900">
                                    {step.title}
                                  </span>
                                  <p className="mt-0.5 text-gray-600">
                                    {step.description}
                                  </p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {step.timeframe && (
                                      <span className="px-1.5 py-0.5 text-[10px] text-blue-800 bg-blue-100 rounded">
                                        {step.timeframe}
                                      </span>
                                    )}
                                    {step.requirements && (
                                      <span className="px-1.5 py-0.5 text-[10px] text-gray-800 bg-gray-100 rounded">
                                        {step.requirements}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ));
                          }
                          
                          // Fallback if no claim steps found
                          return (
                            <li className="flex gap-2 items-start text-xs text-gray-900">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                              <span>Contact support for claim assistance</span>
                            </li>
                          );
                        })()}
                      </ul>
                    </div>
                  </motion.div>
                )}

                {activeTab === "terms" && (
                  <motion.div
                    key="terms"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3"
                  >
                    <div className="p-3 rounded-lg ring-1 ring-emerald-100 bg-emerald-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full flex-shrink-0 mt-0.5">
                          <Shield size={14} />
                        </span>
                        <h4 className="text-xs font-semibold text-gray-900">
                          Terms & Conditions
                        </h4>
                      </div>
                      <div className="pl-7 text-xs leading-relaxed text-gray-700">
                        {(() => {
                          // For Life, General, and Travel insurance, get package-specific terms
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageTerms = selectedPackage?.fullPackage?.terms;
                            
                            if (packageTerms) {
                              // Check if it's a URL or text
                              if (packageTerms.startsWith('http://') || packageTerms.startsWith('https://')) {
                                return (
                                  <a
                                    href={packageTerms}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-emerald-600 hover:underline"
                                  >
                                    View Terms & Conditions
                                  </a>
                                );
                              }
                              return <p className="whitespace-pre-wrap">{packageTerms}</p>;
                            }
                          }
                          
                          // For TripSecure insurance, use policy-level terms
                          const policyTerms = item?.terms || item?.originalPolicy?.terms;
                          if (policyTerms) {
                            if (policyTerms.startsWith('http://') || policyTerms.startsWith('https://')) {
                              return (
                                <a
                                  href={policyTerms}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-emerald-600 hover:underline"
                                >
                                  View Terms & Conditions
                                </a>
                              );
                            }
                            return <p className="whitespace-pre-wrap">{policyTerms}</p>;
                          }
                          
                          // Fallback
                          return <p>Please refer to the policy document for complete terms and conditions.</p>;
                        })()}
                      </div>
                    </div>

                    <div className="p-3 rounded-lg ring-1 ring-amber-100 bg-amber-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-amber-600 bg-amber-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Eligibility Criteria
                          </h4>
                          <p className="mt-0.5 text-[10px] text-gray-600">
                            Requirements for this policy
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-1.5 pl-7">
                        {(() => {
                          // For Life, General, and Travel insurance, get package-specific eligibility
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageEligibility = selectedPackage?.fullPackage?.eligibility_criteria;
                            
                            if (packageEligibility && packageEligibility.length > 0) {
                              return packageEligibility.map((criteria, index) => (
                                <li
                                  key={index}
                                  className="flex gap-2 items-start text-xs"
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                  <span className="text-gray-700">{criteria}</span>
                                </li>
                              ));
                            }
                          }
                          
                          // For TripSecure insurance, use policy-level eligibility
                          const policyEligibility = item?.eligibilityCriteria || item?.originalPolicy?.eligibility_criteria;
                          if (policyEligibility && policyEligibility.length > 0) {
                            return policyEligibility.map((criteria, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                                <span className="text-gray-700">{criteria}</span>
                              </li>
                            ));
                          }
                          
                          // Fallback
                          return (
                            <li className="flex gap-2 items-start text-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                              <span className="text-gray-700">
                                Standard eligibility requirements apply
                              </span>
                            </li>
                          );
                        })()}
                      </ul>
                    </div>

                    <div className="p-3 rounded-lg ring-1 ring-red-100 bg-red-50/60">
                      <div className="flex gap-2 items-start mb-2">
                        <span className="grid place-items-center w-5 h-5 text-red-600 bg-red-100 rounded-full flex-shrink-0 mt-0.5">
                          <FileText size={14} />
                        </span>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900">
                            Exclusions
                          </h4>
                          <p className="mt-0.5 text-[10px] text-gray-600">
                            Situations not covered
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-1.5 pl-7">
                        {(() => {
                          // For Life, General, and Travel insurance, get package-specific exclusions
                          if (isLifeInsurance || isGeneralInsurance || isTravelInsurance) {
                            const selectedPackage = filteredPackages.find(
                              (p) => p.key === selectedPkg
                            );
                            const packageExclusions = selectedPackage?.fullPackage?.exclusions;
                            
                            if (packageExclusions && packageExclusions.length > 0) {
                              return packageExclusions.map((exclusion, index) => (
                                <li
                                  key={index}
                                  className="flex gap-2 items-start text-xs"
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                                  <span className="text-gray-700">{exclusion}</span>
                                </li>
                              ));
                            }
                          }
                          
                          // For TripSecure insurance, use policy-level exclusions
                          const policyExclusions = item?.exclusions || item?.originalPolicy?.exclusions;
                          if (policyExclusions && policyExclusions.length > 0) {
                            return policyExclusions.map((exclusion, index) => (
                              <li
                                key={index}
                                className="flex gap-2 items-start text-xs"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                                <span className="text-gray-700">{exclusion}</span>
                              </li>
                            ));
                          }
                          
                          // Fallback
                          return (
                            <li className="flex gap-2 items-start text-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
                              <span className="text-gray-700">
                                Standard exclusions apply as per policy terms
                              </span>
                            </li>
                          );
                        })()}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.aside>

          {/* Request Quote Modal */}
          {showQuoteModal && (() => {
            const selectedPackage = filteredPackages.find((p) => p.key === selectedPkg);
            const quoteFields = selectedPackage?.quote_request_fields || [];
            const coverageDurationIndex = filteredPackages.findIndex((p) => p.key === selectedPkg);
            const policySlug = item?.originalPolicy?.slug || item?.id;

            const handleQuoteSubmit = async () => {
              const required = quoteFields.filter((f) => f.required);
              const missing = required.filter((f) => !(quoteFormData[f.key] != null && String(quoteFormData[f.key]).trim() !== ""));
              if (missing.length > 0) {
                toast.error(`Please fill in: ${missing.map((f) => f.label).join(", ")}`);
                return;
              }
              setQuoteSubmitting(true);
              const fullPackageObject = selectedPackage?.fullPackage ?? selectedPackage?.fullDuration ?? selectedPackage;
              const result = await quoteService.submitQuoteRequest({
                policy_slug: policySlug,
                coverage_duration_index: String(coverageDurationIndex >= 0 ? coverageDurationIndex : 0),
                package_key: typeof fullPackageObject === "object" && fullPackageObject !== null ? fullPackageObject : selectedPackage?.key,
                form_data: quoteFormData,
              });
              setQuoteSubmitting(false);
              if (result.status) {
                toast.success(result.message || "Quote request submitted. The provider will respond with a quote.");
                setShowQuoteModal(false);
                onClose();
              } else {
                toast.error(result.message || "Failed to submit quote request.");
              }
            };

            return (
              <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50" onClick={() => !quoteSubmitting && setShowQuoteModal(false)}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900">Request a Quote</h3>
                    <button type="button" onClick={() => !quoteSubmitting && setShowQuoteModal(false)} className="p-2 rounded-lg hover:bg-gray-100">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="overflow-y-auto flex-1 p-4">
                    <p className="mb-4 text-sm text-gray-600">
                      {selectedPackage?.productName || "Package"} — provide the details below. The provider will send you a quoted amount.
                    </p>
                    <div className="space-y-4">
                      {quoteFields.map((field) => (
                        <div key={field.key}>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {field.type === "textarea" ? (
                            <textarea
                              value={quoteFormData[field.key] ?? ""}
                              onChange={(e) => setQuoteFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                              rows={3}
                              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            />
                          ) : field.type === "select" ? (
                            <select
                              value={quoteFormData[field.key] ?? ""}
                              onChange={(e) => setQuoteFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                              <option value="">Select...</option>
                              {(Array.isArray(field.options) ? field.options : []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type === "email" ? "email" : field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                              value={quoteFormData[field.key] ?? ""}
                              onChange={(e) => setQuoteFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                              className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end p-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowQuoteModal(false)}
                      disabled={quoteSubmitting}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleQuoteSubmit}
                      disabled={quoteSubmitting}
                      className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg hover:from-amber-600 hover:to-orange-700 disabled:opacity-50"
                    >
                      {quoteSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                      Submit Request
                    </button>
                  </div>
                </motion.div>
              </div>
            );
          })()}
        </>
      )}
    </AnimatePresence>
  );
}
